import React, {
    ReactNode,
    useCallback,
    useId,
    useMemo,
    useRef,
    useState
} from 'react';
import classNames from 'classnames';
import {Modal} from 'reactstrap';
import {FormField, TextField} from './FormField.tsx';
import {ConfirmContext, ConfirmFn, ConfirmOptions} from './confirmContext.ts';

interface DialogProps {
    open: boolean;
    title: ReactNode;
    children: ReactNode;
    // Actions, bottom right, primary last.
    footer?: ReactNode;
    onClose: () => void;
    size?: 'sm' | 'md';
    className?: string;
}

/*
 * A dialog is for a decision that should stop you, or a short task that
 * needs full attention: at most 560px wide, one primary action, Esc closes.
 * Anything you do while looking at your work goes in a SidePanel instead.
 */
export const Dialog: React.FC<DialogProps> = ({
    open,
    title,
    children,
    footer,
    onClose,
    size = 'md',
    className
}) => {
    const titleId = useId();

    return (
        <Modal
            isOpen={open}
            toggle={onClose}
            centered
            labelledBy={titleId}
            className={classNames('mx-dialog', `mx-dialog-${size}`, className)}
            contentClassName="mx-dialog-content"
        >
            <div className="mx-dialog-header">
                <h2 id={titleId} className="mx-dialog-title">
                    {title}
                </h2>
            </div>
            <div className="mx-dialog-body">{children}</div>
            {footer && <div className="mx-dialog-footer">{footer}</div>}
        </Modal>
    );
};

interface ConfirmDialogProps extends ConfirmOptions {
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    busy?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    title,
    body,
    confirmLabel,
    safeLabel = 'Go back',
    danger,
    typeToConfirm,
    onConfirm,
    onCancel,
    busy
}) => {
    const [typed, setTyped] = useState('');
    const canConfirm =
        !busy && (!typeToConfirm || typed.trim() === typeToConfirm.trim());

    return (
        <Dialog
            open={open}
            title={title}
            onClose={onCancel}
            size="sm"
            footer={
                <>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onCancel}
                        autoFocus
                    >
                        {safeLabel}
                    </button>
                    <button
                        type="button"
                        className={classNames('btn', danger ? 'btn-danger' : 'btn-primary')}
                        disabled={!canConfirm}
                        onClick={onConfirm}
                    >
                        {confirmLabel}
                    </button>
                </>
            }
        >
            {body && <p className="mx-dialog-text">{body}</p>}
            {typeToConfirm && (
                <FormField
                    id="mx-confirm-name"
                    label={`Type "${typeToConfirm}" to confirm`}
                >
                    <TextField
                        value={typed}
                        onChange={e => setTyped(e.target.value)}
                        autoComplete="off"
                    />
                </FormField>
            )}
        </Dialog>
    );
};

interface PendingConfirm {
    options: ConfirmOptions;
    resolve: (confirmed: boolean) => void;
}

// Mount once near the root; useConfirm() (confirmContext.ts) then returns a
// promise-based confirm that replaces window.confirm.
export const ConfirmProvider: React.FC<{children: ReactNode}> = ({
    children
}) => {
    const [pending, setPending] = useState<PendingConfirm | null>(null);
    const pendingRef = useRef<PendingConfirm | null>(null);

    const confirm = useCallback<ConfirmFn>(
        options =>
            new Promise<boolean>(resolve => {
                pendingRef.current?.resolve(false);
                const next = {options, resolve};
                pendingRef.current = next;
                setPending(next);
            }),
        []
    );

    const settle = (confirmed: boolean) => {
        pendingRef.current?.resolve(confirmed);
        pendingRef.current = null;
        setPending(null);
    };

    const value = useMemo(() => confirm, [confirm]);

    return (
        <ConfirmContext.Provider value={value}>
            {children}
            {pending && (
                <ConfirmDialog
                    open
                    {...pending.options}
                    onConfirm={() => settle(true)}
                    onCancel={() => settle(false)}
                />
            )}
        </ConfirmContext.Provider>
    );
};
